import { Box, Typography, Snackbar, Slide } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import { useDispatch, useSelector } from "react-redux";

import Translate from "../../../translate/Translate";
import {
  closeSnackbar,
  selectSnackbarStatus
} from "../../../store/reducers/snackbar/openGLSnackbarSlice";
import { COLORS } from "../../v3/Theme/colors";
import {
  getNoticeAlertContainerSx,
  noticeAlertIconSx
} from "../../v3/sharedComponents/noticeAlertStyles";

const VARIANT_CONFIG = {
  error: {
    backgroundColor: COLORS.NOTICE,
    Icon: WarningAmberRoundedIcon,
    iconSx: noticeAlertIconSx
  },
  warning: {
    backgroundColor: COLORS.NOTICE,
    Icon: WarningAmberRoundedIcon,
    iconSx: noticeAlertIconSx
  },
  success: {
    backgroundColor: COLORS.SUCCESS,
    Icon: CheckCircleRoundedIcon,
    iconSx: { ...noticeAlertIconSx, color: COLORS.WHITE }
  },
  info: {
    backgroundColor: COLORS.PRIMARY,
    Icon: InfoRoundedIcon,
    iconSx: { ...noticeAlertIconSx, color: COLORS.WHITE }
  },
  security: {
    backgroundColor: COLORS.NOTICE,
    Icon: LockRoundedIcon,
    iconSx: { ...noticeAlertIconSx, color: COLORS.WHITE }
  }
};

const GLSnackbar = () => {
  const dispatch = useDispatch();
  const snackbarStatus = useSelector(selectSnackbarStatus);
  const open = Boolean(snackbarStatus?.open);
  const variant = snackbarStatus?.variant || "info";
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.info;
  const { backgroundColor, Icon, iconSx } = config;

  const handleClose = (_event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    dispatch(closeSnackbar());
  };

  const infoMessage = (v) => {
    if (v) {
      const lower = v.toLowerCase();
      return `${v.charAt(0).toUpperCase() + lower.slice(1)} message`;
    }
    return "";
  };

  const headerText =
    snackbarStatus?.header && snackbarStatus.header.length > 0
      ? snackbarStatus.header
      : null;
  const subText =
    snackbarStatus?.subText && snackbarStatus.subText.length > 0
      ? snackbarStatus.subText
      : infoMessage(snackbarStatus?.variant);

  const SnackbarContent = (
    <Box
      sx={{
        ...getNoticeAlertContainerSx(backgroundColor),
        maxWidth: "min(560px, calc(100vw - 32px))"
      }}
      role="alert"
      aria-live="polite"
    >
      <Icon sx={iconSx} aria-hidden />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          minWidth: 0,
          textAlign: "left"
        }}
      >
        {headerText && (
          <Typography
            component="span"
            variant="body_strong"
            sx={{ color: COLORS.WHITE }}
          >
            <Translate text={headerText} />
          </Typography>
        )}
        {subText && (
          <Typography
            component="span"
            variant={headerText ? "body2" : "body_strong"}
            sx={{
              color: COLORS.WHITE,
              ...(headerText ? { fontWeight: 500, lineHeight: 1.45 } : {})
            }}
          >
            <Translate text={subText} />
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Snackbar
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center"
      }}
      open={open}
      autoHideDuration={6000}
      onClose={handleClose}
      ContentProps={{
        sx: {
          backgroundColor: "transparent",
          boxShadow: "none",
          padding: 0,
          minWidth: 0
        }
      }}
    >
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <div>{SnackbarContent}</div>
      </Slide>
    </Snackbar>
  );
};

export default GLSnackbar;
